import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import {
    X,
    Crop,
    RotateCcw,
    RotateCw,
    FlipHorizontal,
    FlipVertical,
    Undo2,
    Redo2,
    Sparkles,
    Save,
    Loader2,
} from 'lucide-react';
import ImageEditor from 'tui-image-editor';
import 'tui-image-editor/dist/tui-image-editor.css';
import { generateImage, fetchModels } from '@/services/imageEditService';
import { uploadImageToStorage } from '@/services/storageService';
import { compressImageInBrowser, compressionMaxBytes } from '@/services/imageCompressionService';
import { useConfigStore } from '@/store/useConfigStore';
import { Select, Textarea } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import type { ImageModelInfo, ImageJobData } from '@/services/imageEditService';
import type { ImageEditInstance } from '@/types';

// --- Props ---

interface ImageEditorModalProps {
    open: boolean;
    onClose: () => void;
    imageUrl: string;
    readOnly?: boolean;
    onSave?: (newImageUrl: string) => void;
}

// --- 比例选项 ---

const RATIO_OPTIONS = [
    { value: '1:1', label: '1:1' },
    { value: '3:2', label: '3:2' },
    { value: '2:3', label: '2:3' },
    { value: '4:3', label: '4:3' },
    { value: '3:4', label: '3:4' },
    { value: '16:9', label: '16:9' },
    { value: '9:16', label: '9:16' },
];

const QUALITY_OPTIONS = [
    { value: 'standard', label: '标准' },
    { value: 'hd', label: 'HD' },
    { value: 'ultra', label: 'Ultra' },
    { value: '4k', label: '4K' },
];

// 将可能是相对路径的 URL 转为完整代理 URL
function resolveImageUrl(url: string, baseUrl?: string): string {
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return `/api/proxy?target=${encodeURIComponent(url)}`;
    }
    // 相对路径，拼接 baseUrl 后走代理
    if (baseUrl) {
        const base = baseUrl.replace(/\/$/, '');
        const fullUrl = `${base}${url.startsWith('/') ? '' : '/'}${url}`;
        return `/api/proxy?target=${encodeURIComponent(fullUrl)}`;
    }
    // 无法解析，直接返回原 URL
    return url;
}

// --- 工具栏按钮 ---

function ToolbarButton({
    icon: Icon,
    label,
    onClick,
    active,
    disabled,
}: {
    icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number | string }>;
    label: string;
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
}) {
    return (
        <button
            title={label}
            onClick={onClick}
            disabled={disabled}
            className={`flex h-9 w-9 items-center justify-center rounded-md text-zinc-600 transition-colors
                ${active ? 'bg-indigo-100 text-indigo-600' : 'hover:bg-zinc-100 hover:text-zinc-900'}
                ${disabled ? 'cursor-not-allowed opacity-40' : ''}
            `}
        >
            <Icon size={18} />
        </button>
    );
}

// --- AI 结果缩略图（直接展示前后对比） ---

function AIResultThumb({ src, originalSrc, onClick }: { src: string; originalSrc: string; onClick: () => void }) {
    return (
        <div className="flex gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 p-1.5">
            <div className="flex flex-1 flex-col items-center gap-0.5">
                <span className="text-[10px] font-medium text-zinc-400">原图</span>
                <img src={originalSrc} alt="" className="w-full rounded border border-zinc-100 object-contain" />
            </div>
            <div className="flex flex-1 flex-col items-center gap-0.5">
                <span className="text-[10px] font-medium text-violet-500">AI 生成</span>
                <img
                    src={src}
                    alt="AI 生成结果"
                    className="w-full cursor-pointer rounded border border-violet-200 object-cover hover:ring-2 hover:ring-violet-400"
                    onClick={onClick}
                />
            </div>
        </div>
    );
}

// --- AI 编辑面板 ---

function AIEditPanel({
    editorRef,
    onClose,
    imageUrl,
}: {
    editorRef: React.MutableRefObject<ImageEditor | null>;
    onClose: () => void;
    imageUrl: string;
}) {
    const { imageEditInstances, imageEditSettings } = useConfigStore();
    const presetPrompts = imageEditSettings.presetPrompts ?? [];
    const defaultPrompt = presetPrompts[0] ?? '';
    const [prompt, setPrompt] = useState(defaultPrompt);
    const [instanceId, setInstanceId] = useState<string>(imageEditInstances[0]?.id ?? '');
    const [models, setModels] = useState<ImageModelInfo[]>([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [ratio, setRatio] = useState('1:1');
    const [quality, setQuality] = useState('standard');
    const count = 1;
    const [generating, setGenerating] = useState(false);
    const [progressStatus, setProgressStatus] = useState('');
    const [error, setError] = useState('');
    const [results, setResults] = useState<ImageJobData | null>(null);

    const selectedInstance = imageEditInstances.find(i => i.id === instanceId);

    // 加载模型列表
    useEffect(() => {
        if (!selectedInstance) return;
        let cancelled = false;
        fetchModels(selectedInstance)
            .then(list => {
                if (cancelled) return;
                setModels(list);
                if (list.length > 0) setSelectedModel(list[0].id);
            })
            .catch(() => {
                if (!cancelled) setModels([]);
            });
        return () => { cancelled = true; };
    }, [selectedInstance]);

    const handleGenerate = useCallback(async () => {
        if (!selectedInstance || !prompt.trim()) return;

        setGenerating(true);
        setError('');
        setResults(null);
        setProgressStatus('queued');

        try {
            // 将当前编辑器画布转为 File
            let images: File[] | undefined;
            if (editorRef.current) {
                const dataURL = editorRef.current.toDataURL({ format: 'png' });
                const blob = await (await fetch(dataURL)).blob();
                const file = new File([blob], 'reference.png', { type: 'image/png' });
                images = [file];
            }

            const instance: ImageEditInstance = {
                ...selectedInstance,
                model: selectedModel || selectedInstance.model,
            };

            const job = await generateImage(
                instance,
                {
                    prompt: prompt.trim(),
                    images,
                    ratio,
                    quality,
                    count,
                },
                (status) => setProgressStatus(status),
            );

            setResults(job);

            // 加载第一张结果到编辑器
            if (editorRef.current && job.result?.data?.[0]) {
                const item = job.result.data[0];
                const imgUrl = item.url
                    ? resolveImageUrl(item.url, selectedInstance.apiUrl)
                    : item.b64_json
                        ? `data:image/png;base64,${item.b64_json}`
                        : '';
                if (imgUrl) {
                    await editorRef.current.loadImageFromURL(imgUrl, 'ai-result');
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : '生成失败');
        } finally {
            setGenerating(false);
            setProgressStatus('');
        }
    }, [selectedInstance, selectedModel, prompt, ratio, quality, editorRef]);

    return (
        <div className="flex h-full w-80 flex-col border-l border-zinc-200 bg-white">
            {/* 头部 */}
            <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-violet-500" />
                    <span className="text-sm font-semibold text-zinc-800">AI 编辑</span>
                </div>
                <button onClick={onClose} className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600">
                    <X size={16} />
                </button>
            </div>

            {/* 表单 */}
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
                {/* 预设提示词选择 */}
                {presetPrompts.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-zinc-600">预设提示词</label>
                        <select
                            value={presetPrompts.includes(prompt) ? prompt : '__custom__'}
                            onChange={e => { if (e.target.value !== '__custom__') setPrompt(e.target.value); }}
                            className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                        >
                            <option value="__custom__">自定义提示词</option>
                            {presetPrompts.map((p, i) => (
                                <option key={i} value={p}>{p.length > 40 ? p.slice(0, 40) + '…' : p}</option>
                            ))}
                        </select>
                    </div>
                )}
                <Textarea
                    label="提示词"
                    placeholder="描述你想要的编辑效果…"
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    rows={3}
                />

                <Select
                    label="实例"
                    value={instanceId}
                    onChange={e => setInstanceId(e.target.value)}
                >
                    {imageEditInstances.map(inst => (
                        <option key={inst.id} value={inst.id}>{inst.name}</option>
                    ))}
                </Select>

                <Select
                    label="模型"
                    value={selectedModel}
                    onChange={e => setSelectedModel(e.target.value)}
                >
                    {models.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                </Select>

                <Select
                    label="比例"
                    value={ratio}
                    onChange={e => setRatio(e.target.value)}
                >
                    {RATIO_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </Select>

                <Select
                    label="质量"
                    value={quality}
                    onChange={e => setQuality(e.target.value)}
                >
                    {QUALITY_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </Select>

                {/* 进度 */}
                {generating && (
                    <div className="flex items-center gap-2 rounded-md bg-violet-50 px-3 py-2 text-xs text-violet-700">
                        <Loader2 size={14} className="animate-spin" />
                        <span>
                            {progressStatus === 'queued' && '排队中…'}
                            {progressStatus === 'running' && '生成中…'}
                            {!progressStatus && '准备中…'}
                        </span>
                    </div>
                )}

                {/* 错误 */}
                {error && (
                    <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                        {error}
                    </div>
                )}

                {/* 结果缩略图 */}
                {results?.result?.data && results.result.data.length > 0 && (
                    <div className="space-y-1">
                        <span className="text-xs font-medium text-zinc-600">生成结果</span>
                        <div className="grid grid-cols-1 gap-2">
                            {results.result.data.map((item, idx) => {
                                const src = item.url
                                    ? resolveImageUrl(item.url, selectedInstance?.apiUrl)
                                    : item.b64_json
                                        ? `data:image/png;base64,${item.b64_json}`
                                        : '';
                                return src ? (
                                    <AIResultThumb
                                        key={idx}
                                        src={src}
                                        originalSrc={imageUrl}
                                        onClick={async () => {
                                            if (editorRef.current) {
                                                await editorRef.current.loadImageFromURL(src, `ai-result-${idx}`);
                                            }
                                        }}
                                    />
                                ) : null;
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* 生成按钮 */}
            <div className="border-t border-zinc-200 px-4 py-3">
                <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleGenerate}
                    disabled={generating || !prompt.trim() || !selectedInstance}
                >
                    {generating ? (
                        <>
                            <Loader2 size={14} className="animate-spin" />
                            生成中
                        </>
                    ) : (
                        <>
                            <Sparkles size={14} />
                            生成
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
}

// --- 主组件 ---

export function ImageEditorModal({ open, onClose, imageUrl, readOnly = false, onSave }: ImageEditorModalProps) {
    const editorRef = useRef<ImageEditor | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [cropMode, setCropMode] = useState(false);
    const [aiPanelOpen, setAiPanelOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [loading, setLoading] = useState(true);

    const { imageEditSettings } = useConfigStore();

    // 初始化 / 销毁编辑器
    useEffect(() => {
        if (!open || !containerRef.current) return;

        const container = containerRef.current;
        // 获取容器尺寸
        const rect = container.getBoundingClientRect();

        let destroyed = false;

        const init = async () => {
            const editor = new ImageEditor(container, {
                cssMaxWidth: rect.width,
                cssMaxHeight: rect.height,
                usageStatistics: false,
            });

            editorRef.current = editor;

            // 通过代理加载图片避免 CORS
            const imgSrc = imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')
                ? imageUrl
                : `/api/proxy?target=${encodeURIComponent(imageUrl)}`;

            try {
                await editor.loadImageFromURL(imgSrc, 'image');
            } catch (e) {
                console.warn('[ImageEditorModal] 代理加载失败，尝试直接加载:', e);
                // 如果代理失败，尝试直接加载
                try {
                    await editor.loadImageFromURL(imageUrl, 'image');
                } catch (e2) {
                    console.error('[ImageEditorModal] 图片加载失败:', e2);
                }
            }

            if (!destroyed) {
                setLoading(false);
            }
        };

        init();

        return () => {
            destroyed = true;
            if (editorRef.current) {
                editorRef.current.destroy();
                editorRef.current = null;
            }
            setLoading(true);
            setCropMode(false);
            setAiPanelOpen(false);
        };
    }, [open, imageUrl]);

    // 让编辑器画布在左侧编辑区域居中
    useEffect(() => {
        if (!open || !containerRef.current) return;

        const applyCentering = () => {
            const container = containerRef.current;
            if (!container) return;
            const cc = container.querySelector('.tui-image-editor-canvas-container') as HTMLElement;
            if (!cc) return;

            const maxW = parseInt(cc.style.maxWidth) || 0;
            const maxH = parseInt(cc.style.maxHeight) || 0;
            const pw = container.clientWidth;
            const ph = container.clientHeight;

            const ml = (maxW && maxW < pw) ? Math.floor((pw - maxW) / 2) : 0;
            const mt = (maxH && maxH < ph) ? Math.floor((ph - maxH) / 2) : 0;

            // 避免重复设置相同值触发重排
            if (cc.style.marginLeft !== `${ml}px`) cc.style.marginLeft = `${ml}px`;
            if (cc.style.marginTop !== `${mt}px`) cc.style.marginTop = `${mt}px`;
        };

        const t1 = setTimeout(applyCentering, 200);
        const t2 = setTimeout(applyCentering, 500);
        const ro = new ResizeObserver(applyCentering);
        ro.observe(containerRef.current);

        return () => { clearTimeout(t1); clearTimeout(t2); ro.disconnect(); };
    }, [open]);

    // 不打开时不渲染
    if (!open) return null;

    // --- 工具栏操作 ---

    const handleCrop = () => {
        if (!editorRef.current) return;
        if (cropMode) {
            // 应用裁剪
            const editor = editorRef.current;
            const cropRect = editor.getCropzoneRect();
            if (cropRect) {
                editor.stopDrawingMode();
                editor.crop(cropRect);
                setCropMode(false);
                return;
            }
            editor.stopDrawingMode();
            setCropMode(false);
        } else {
            editorRef.current.startDrawingMode('CROPPER');
            setCropMode(true);
        }
    };

    const handleRotateLeft = () => {
        if (!editorRef.current) return;
        editorRef.current.rotate(-90);
    };

    const handleRotateRight = () => {
        if (!editorRef.current) return;
        editorRef.current.rotate(90);
    };

    const handleFlipH = () => {
        if (!editorRef.current) return;
        editorRef.current.flipX();
    };

    const handleFlipV = () => {
        if (!editorRef.current) return;
        editorRef.current.flipY();
    };

    const handleUndo = () => {
        if (!editorRef.current) return;
        editorRef.current.undo();
    };

    const handleRedo = () => {
        if (!editorRef.current) return;
        editorRef.current.redo();
    };

    const handleSave = async () => {
        if (!editorRef.current || saving) return;
        setSaving(true);
        setSaveError('');

        try {
            const dataURL = editorRef.current.toDataURL({ format: 'png' });
            const compressed = imageEditSettings.browserCompressionEnabled
                ? await compressImageInBrowser(dataURL, compressionMaxBytes(imageEditSettings.compressionLevel))
                : null;

            if (imageEditSettings.jdcloudStorageEnabled) {
                const uploadedUrl = await uploadImageToStorage({
                    fileName: compressed?.fileName ?? 'edited-image.png',
                    fileBase64: compressed?.fileBase64 ?? dataURL.replace(/^data:image\/\w+;base64,/, ''),
                    contentType: compressed?.contentType ?? 'image/png',
                });
                onSave?.(uploadedUrl);
            } else {
                onSave?.(compressed?.dataUrl ?? dataURL);
            }

            onClose();
        } catch (err) {
            console.error('[ImageEditorModal] 保存失败:', err);
            setSaveError(err instanceof Error ? err.message : '图片保存失败');
        } finally {
            setSaving(false);
        }
    };

    // --- 渲染 ---

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center animate-fadeIn">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div
                className="relative z-10 flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl animate-scaleIn"
                style={{ width: 'min(90vw, 1100px)', height: 'min(88vh, 780px)' }}
            >
                {/* 顶部工具栏 */}
                <div className="flex h-12 shrink-0 items-center gap-1 border-b border-zinc-200 bg-white px-2">
                    {/* 关闭按钮 - 始终可见 */}
                    <button
                        onClick={onClose}
                        className="mr-2 flex h-9 w-9 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 transition-colors"
                        title="关闭"
                    >
                        <X size={18} />
                    </button>

                    {!readOnly && (
                        <>
                            <div className="mx-1 h-5 w-px bg-zinc-200" />

                            <ToolbarButton icon={Crop} label={cropMode ? '应用裁剪' : '裁剪'} onClick={handleCrop} active={cropMode} />
                            <ToolbarButton icon={RotateCcw} label="逆时针旋转 90°" onClick={handleRotateLeft} />
                            <ToolbarButton icon={RotateCw} label="顺时针旋转 90°" onClick={handleRotateRight} />
                            <ToolbarButton icon={FlipHorizontal} label="水平翻转" onClick={handleFlipH} />
                            <ToolbarButton icon={FlipVertical} label="垂直翻转" onClick={handleFlipV} />

                            <div className="mx-1 h-5 w-px bg-zinc-200" />

                            <ToolbarButton icon={Undo2} label="撤销" onClick={handleUndo} />
                            <ToolbarButton icon={Redo2} label="重做" onClick={handleRedo} />

                            <div className="mx-1 h-5 w-px bg-zinc-200" />

                            <ToolbarButton
                                icon={Sparkles}
                                label="AI 编辑"
                                onClick={() => setAiPanelOpen(v => !v)}
                                active={aiPanelOpen}
                            />

                            {/* 保存按钮 - 最右侧 */}
                            <div className="ml-auto flex items-center gap-3">
                                {saveError && (
                                    <span className="max-w-72 truncate text-xs text-red-600" title={saveError}>{saveError}</span>
                                )}
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex h-9 items-center gap-1.5 rounded-md bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
                                >
                                    {saving ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                        <Save size={14} />
                                    )}
                                    {saving ? '保存中…' : '保存'}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* 编辑区 + AI 面板 */}
                <div className="flex flex-1 min-h-0 bg-zinc-50">
                    {/* 画布区域 */}
                    <div className="relative flex-1">
                        {loading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-zinc-50/80">
                                <div className="flex flex-col items-center gap-2 text-zinc-500">
                                    <Loader2 size={24} className="animate-spin" />
                                    <span className="text-sm">加载图片中…</span>
                                </div>
                            </div>
                        )}
                        <div ref={containerRef} className="h-full w-full" />
                    </div>

                    {/* AI 编辑侧面板 */}
                    {aiPanelOpen && !readOnly && (
                        <AIEditPanel
                            editorRef={editorRef}
                            onClose={() => setAiPanelOpen(false)}
                            imageUrl={imageUrl}
                        />
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
